import React, { useState } from "react";
import { Phone, MessageSquare, Send, Sparkles, Instagram, Loader2, CheckCircle2, MapPin, Navigation } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useData } from "@/context/DataContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function ContactPage() {
  const { settings } = useData();
  const navigate = useNavigate();
  const phone = settings?.phone || "207-773-8000";
  const digits = phone.replace(/[^0-9]/g, "");
  const addr = settings?.address || "135 Congress St, Portland, ME 04101";
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.message) return toast.error("Please fill name, email and message");
    setLoading(true);
    try {
      await api.post("/contact", f);
      setSent(true);
      toast.success("Message sent!");
    } catch { toast.error("Something went wrong"); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <section className="relative py-20 bg-gradient-to-b from-emerald-950 to-[#101311] tropical-grain">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <h1 className="font-display font-black uppercase tracking-tight text-4xl sm:text-6xl">Get In Touch</h1>
          <p className="text-[#FDFBF7]/75 mt-4 text-lg">Questions, orders, or catering — we're here.</p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-14 grid md:grid-cols-2 gap-8">
        <div className="space-y-3">
          <a data-testid="contact-call-button" href={`tel:+1${digits}`} className="flex items-center gap-4 rounded-2xl bg-[#181D1A] border border-emerald-900/60 hover:border-amber-500/60 p-5 transition-colors">
            <span className="grid place-items-center w-12 h-12 rounded-full bg-amber-400 text-[#0A2818]"><Phone className="w-6 h-6" /></span>
            <div><div className="text-xs uppercase tracking-wider text-amber-400 font-bold">Call Us</div><div className="font-display font-bold text-xl">{phone}</div></div>
          </a>
          <a data-testid="contact-text-button" href={`sms:${digits}`} className="flex items-center gap-4 rounded-2xl bg-[#181D1A] border border-emerald-900/60 hover:border-amber-500/60 p-5 transition-colors">
            <span className="grid place-items-center w-12 h-12 rounded-full bg-emerald-700 text-[#FDFBF7]"><MessageSquare className="w-6 h-6" /></span>
            <div><div className="text-xs uppercase tracking-wider text-amber-400 font-bold">Text Us</div><div className="font-display font-bold text-xl">{phone}</div></div>
          </a>
          <button data-testid="contact-catering-button" onClick={() => navigate("/catering")} className="w-full flex items-center gap-4 rounded-2xl bg-[#181D1A] border border-emerald-900/60 hover:border-amber-500/60 p-5 transition-colors text-left">
            <span className="grid place-items-center w-12 h-12 rounded-full bg-[#E05A2B] text-[#FDFBF7]"><Sparkles className="w-6 h-6" /></span>
            <div><div className="text-xs uppercase tracking-wider text-amber-400 font-bold">Catering</div><div className="font-display font-bold text-xl">Request A Quote</div></div>
          </button>
          <a data-testid="contact-address" href={`https://maps.google.com/?q=${encodeURIComponent(addr)}`} target="_blank" rel="noreferrer" className="flex items-center gap-4 rounded-2xl bg-[#181D1A] border border-emerald-900/60 hover:border-amber-500/60 p-5 transition-colors">
            <span className="grid place-items-center w-12 h-12 rounded-full bg-emerald-700 text-[#FDFBF7]"><MapPin className="w-6 h-6" /></span>
            <div><div className="text-xs uppercase tracking-wider text-amber-400 font-bold">Visit Us</div><div className="font-display font-bold text-lg">{addr}</div></div>
          </a>
          <a data-testid="contact-directions-button" href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addr)}`} target="_blank" rel="noreferrer" className="w-full flex items-center justify-center gap-2 rounded-2xl bg-amber-400 hover:bg-amber-300 text-[#0A2818] font-display font-black uppercase tracking-wide p-4 transition-colors active:scale-95">
            <Navigation className="w-5 h-5" /> Get Directions
          </a>
          <div className="flex gap-3 pt-2">
            {settings?.social?.instagram && <a href={`https://instagram.com/${settings.social.instagram}`} target="_blank" rel="noreferrer" className="grid place-items-center w-11 h-11 rounded-full bg-emerald-900/70 text-amber-300 hover:bg-amber-400 hover:text-[#0A2818] transition-colors"><Instagram className="w-5 h-5" /></a>}
            {settings?.social?.tiktok && <a href={`https://tiktok.com/@${settings.social.tiktok}`} target="_blank" rel="noreferrer" className="grid place-items-center w-11 h-11 rounded-full bg-emerald-900/70 text-amber-300 hover:bg-amber-400 hover:text-[#0A2818] transition-colors"><i className="fa-brands fa-tiktok" /></a>}
            {settings?.social?.snapchat && <a href={`https://snapchat.com/add/${settings.social.snapchat}`} target="_blank" rel="noreferrer" className="grid place-items-center w-11 h-11 rounded-full bg-emerald-900/70 text-amber-300 hover:bg-amber-400 hover:text-[#0A2818] transition-colors"><i className="fa-brands fa-snapchat" /></a>}
          </div>
        </div>

        {sent ? (
          <div className="rounded-3xl bg-[#181D1A] border border-amber-500/40 p-8 grid place-items-center text-center">
            <div><CheckCircle2 className="w-12 h-12 text-amber-400 mx-auto" /><h3 className="font-display font-black text-2xl uppercase mt-3">Message Sent!</h3><p className="text-[#FDFBF7]/70 mt-2">We'll get back to you soon.</p></div>
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-3xl bg-[#181D1A] border border-emerald-900/60 p-6 space-y-4">
            <h2 className="font-display font-black text-2xl uppercase text-amber-400">Send A Message</h2>
            <input data-testid="contact-name" value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Name *" className={inp} />
            <input data-testid="contact-email" type="email" autoComplete="email" value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="Email *" className={inp} />
            <input data-testid="contact-phone" type="tel" autoComplete="tel" value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Phone" className={inp} />
            <textarea data-testid="contact-message" rows={5} value={f.message} onChange={(e) => set("message", e.target.value)} placeholder="How can we help? *" className={inp} />
            <button data-testid="contact-submit-button" disabled={loading} className="w-full py-4 rounded-full bg-amber-400 hover:bg-amber-300 disabled:opacity-40 text-[#0A2818] font-display font-black uppercase tracking-wide flex items-center justify-center gap-2 active:scale-95 transition-transform">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />} Send Message
            </button>
          </form>
        )}
      </section>

      <section className="max-w-5xl mx-auto px-5 sm:px-8 pb-16">
        <div className="rounded-3xl overflow-hidden border border-emerald-900/60" data-testid="contact-map">
          <iframe
            title="Sir. King Kitchen location map"
            src={`https://www.google.com/maps?q=${encodeURIComponent(addr)}&output=embed`}
            className="w-full h-[340px] sm:h-[420px]"
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </div>
      </section>
    </div>
  );
}
const inp = "w-full bg-[#101311] rounded-xl px-4 py-3 text-sm border border-emerald-800/50 outline-none focus:border-amber-400";
